/**
 * Token存储服务
 * 用于管理JWT令牌的存储、获取和删除
 * 支持30天持久化登录
 */

import { createModuleLogger } from '@utils/logger';

const log = createModuleLogger('Token');

// 访问令牌存储在localStorage中，实现30天持久化登录
const ACCESS_TOKEN_KEY = 'accessToken';
// 刷新令牌存储在localStorage中，持久化存储
const REFRESH_TOKEN_KEY = 'refreshToken';
// 登录时间戳存储key
const LOGIN_TIMESTAMP_KEY = 'loginTimestamp';
// 登录有效期（30天）
const LOGIN_EXPIRY_DAYS = 30;
const LOGIN_EXPIRY_MS = LOGIN_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

/**
 * 保存访问令牌（支持30天持久化）
 * @param {string} token - 访问令牌
 * @param {boolean} rememberMe - 是否记住登录状态（30天）
 */
export function saveAccessToken(token, rememberMe = true) {
  if (token) {
    if (rememberMe) {
      // 30天持久化登录：存储到 localStorage
      localStorage.setItem(ACCESS_TOKEN_KEY, token);
      localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());
      log.info('已保存访问令牌到 localStorage，支持30天持久化');
    } else {
      // 仅当前会话：存储到 sessionStorage
      sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
      log.info('已保存访问令牌到 sessionStorage，仅当前会话有效');
    }
  }
}

/**
 * 获取访问令牌（自动检查30天过期和JWT过期）
 * @returns {string|null} 访问令牌
 */
export function getAccessToken() {
  log.debug('getAccessToken() 被调用');

  // 优先从 localStorage 获取（持久化登录）
  let token = localStorage.getItem(ACCESS_TOKEN_KEY);
  let isFromPersistent = true;

  log.debug('localStorage 中的 accessToken', { exists: !!token });

  if (!token) {
    // 如果 localStorage 没有，从 sessionStorage 获取（会话登录）
    token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    isFromPersistent = false;
    log.debug('sessionStorage 中的 accessToken', { exists: !!token });
  }

  if (!token) {
    log.debug('没有找到任何 accessToken');
    return null;
  }

  // 首先检查JWT token本身是否过期
  if (isTokenExpired(token)) {
    log.info('JWT 令牌已过期，清理过期令牌');
    clearTokens();
    return null;
  }

  // 如果是持久化登录，检查30天过期
  if (isFromPersistent) {
    const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
    log.debug('loginTimestamp', { loginTimestamp });
    if (loginTimestamp) {
      const loginTime = parseInt(loginTimestamp, 10);
      const now = Date.now();
      const timeDiff = now - loginTime;

      if (timeDiff > LOGIN_EXPIRY_MS) {
        log.info('30天登录已过期，清理过期令牌');
        clearTokens();
        return null;
      }

      const remainingDays = Math.ceil((LOGIN_EXPIRY_MS - timeDiff) / (24 * 60 * 60 * 1000));
      log.debug('持久化登录有效', { remainingDays });
    }
  }

  return token;
}

/**
 * 删除访问令牌
 */
export function removeAccessToken() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  log.info('已清理所有访问令牌');
}

/**
 * 保存刷新令牌
 * @param {string} token - 刷新令牌
 */
export function saveRefreshToken(token) {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }
}

/**
 * 获取刷新令牌
 * @returns {string|null} 刷新令牌
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * 删除刷新令牌
 */
export function removeRefreshToken() {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * 清除所有令牌和登录状态
 */
export function clearTokens() {
  removeAccessToken();
  removeRefreshToken();
  localStorage.removeItem(LOGIN_TIMESTAMP_KEY);
  log.info('已清理所有令牌和登录状态');
}

/**
 * 检查是否有访问令牌
 * @returns {boolean} 是否有访问令牌
 */
export function hasAccessToken() {
  return !!getAccessToken();
}

/**
 * 检查是否有刷新令牌
 * @returns {boolean} 是否有刷新令牌
 */
export function hasRefreshToken() {
  return !!getRefreshToken();
}

/**
 * 解析JWT令牌
 * @param {string} token - JWT令牌
 * @returns {Object|null} 解析后的令牌负载
 */
export function parseToken(token) {
  if (!token) {
    return null;
  }

  try {
    // JWT令牌格式：header.payload.signature
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    log.error('解析令牌失败', error);
    return null;
  }
}

/**
 * 获取令牌过期时间
 * @param {string} token - JWT令牌
 * @returns {number|null} 过期时间戳（毫秒）
 */
export function getTokenExpiration(token) {
  const payload = parseToken(token);
  return payload ? payload.exp * 1000 : null; // 转换为毫秒
}

/**
 * 检查令牌是否过期
 * @param {string} token - JWT令牌
 * @returns {boolean} 是否过期
 */
export function isTokenExpired(token) {
  const expiration = getTokenExpiration(token);
  return expiration ? Date.now() > expiration : true;
}

/**
 * 获取令牌中的用户ID
 * @param {string} token - JWT令牌
 * @returns {number|null} 用户ID
 */
export function getUserIdFromToken(token) {
  const payload = parseToken(token);
  return payload ? payload.userId : null;
}

import { USER_ROLE_ADMIN, hasDevToolsAccess } from '@/constants/userRole';

/**
 * 从JWT令牌中获取用户角色
 * @param {string} token - JWT令牌
 * @returns {number|null} 用户角色
 */
export function getUserRoleFromToken(token) {
  const payload = parseToken(token);
  if (!payload) return null;
  
  // JWT中的角色字段名是 "role"，需要转换为数字
  const role = payload.role || payload.userRole;
  return role ? parseInt(role, 10) : null;
}

/**
 * 检查当前用户是否为管理员
 * @returns {boolean} 是否为管理员
 */
export function isCurrentUserAdmin() {
  const accessToken = getAccessToken();
  if (!accessToken) return false;

  const userRole = getUserRoleFromToken(accessToken);
  return userRole === USER_ROLE_ADMIN;
}

/**
 * 获取当前用户信息
 * @returns {Object|null} 用户信息
 */
export function getCurrentUserInfo() {
  const accessToken = getAccessToken();
  if (!accessToken) return null;

  const payload = parseToken(accessToken);
  if (!payload) return null;
  
  // 获取用户角色，JWT中字段名是 "role"
  const userRole = getUserRoleFromToken(accessToken);
  
  return {
    userId: payload.userId || payload.sub, // sub是JWT标准字段，存储用户ID
    userRole: userRole,
    username: payload.username || payload.userAccount,
    isAdmin: userRole === USER_ROLE_ADMIN,
    hasDevTools: hasDevToolsAccess(userRole)
  };
}

/**
 * 获取当前用户ID
 * @returns {number|null} 用户ID
 */
export function getCurrentUserId() {
  const userInfo = getCurrentUserInfo();
  return userInfo ? userInfo.userId : null;
}

/**
 * 检查是否为持久化登录
 * @returns {boolean} 是否为持久化登录
 */
export function isPersistentLogin() {
  return !!localStorage.getItem(ACCESS_TOKEN_KEY) && !!localStorage.getItem(LOGIN_TIMESTAMP_KEY);
}

/**
 * 获取登录剩余天数
 * @returns {number} 剩余天数，-1表示非持久化登录或已过期
 */
export function getRemainingLoginDays() {
  if (!isPersistentLogin()) {
    return -1;
  }
  
  const loginTimestamp = localStorage.getItem(LOGIN_TIMESTAMP_KEY);
  if (!loginTimestamp) {
    return -1;
  }
  
  const loginTime = parseInt(loginTimestamp, 10);
  const now = Date.now();
  const timeDiff = now - loginTime;
  
  if (timeDiff > LOGIN_EXPIRY_MS) {
    return 0; // 已过期
  }
  
  return Math.ceil((LOGIN_EXPIRY_MS - timeDiff) / (24 * 60 * 60 * 1000));
}

/**
 * 刷新登录时间戳（用于用户活跃时延长登录时间）
 */
export function refreshLoginTimestamp() {
  if (isPersistentLogin()) {
    localStorage.setItem(LOGIN_TIMESTAMP_KEY, Date.now().toString());
    log.debug('已刷新登录时间戳，重新30天计时');
  }
}

export default {
  saveAccessToken,
  getAccessToken,
  removeAccessToken,
  saveRefreshToken,
  getRefreshToken,
  removeRefreshToken,
  clearTokens,
  hasAccessToken,
  hasRefreshToken,
  parseToken,
  getTokenExpiration,
  isTokenExpired,
  getUserIdFromToken,
  getUserRoleFromToken,
  isCurrentUserAdmin,
  getCurrentUserInfo,
  getCurrentUserId
};
